import { useEffect, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useBatches, useProducts, useProductVersions, useSuppliers } from '../data/catalog.ts'
import { Banner, NotAllowed } from '../ui/components.tsx'
import { RowLink } from '../ui/RowLink.tsx'
import { ProductsTab } from './catalogue/ProductsTab.tsx'
import { StockTab } from './catalogue/StockTab.tsx'
import { SuppliersTab } from './catalogue/SuppliersTab.tsx'

// M15-A: the Stock tab (on hand per product and place, low rows first, "engagé", the receipt) between the products and the suppliers; `?tab=stock` opens it
const TABS = ['products', 'stock', 'suppliers'] as const
type Tab = (typeof TABS)[number]
const isTab = (v: unknown): v is Tab => typeof v === 'string' && (TABS as readonly string[]).includes(v)

/** The catalogue (M9): products with immutable versions, their batches, and the suppliers. Reading works from the device's own copy; every write is an online command (the workshop
 *  and the storeroom), and the screen shows the server's verdict. There is no money in it. A quantity converted here is a display aid: the server converts again when a part is recorded.
 *  M23 item 24: without catalog.read the page is the same "Vous n'avez pas accès" card as every page that is not the profile's. */
export function CataloguePage() {
  const { t } = useTranslation()
  const { can } = useClub()
  const online = useEngineState().online !== false
  const search = useSearch({ strict: false }) as { p?: string; tab?: string }
  const focusId = search.p
  const [tab, setTab] = useState<Tab>(isTab(search.tab) ? search.tab : 'products')
  // the address changes while the page stays mounted (the Stock tab's "Voir le produit" links to /catalogue?p=…; a row's link to ?tab=): the tab follows it
  // (M15a ergonomics review: found by the storekeeper's walk — the product never opened from the Stock tab, the page kept its tab state)
  useEffect(() => { if (search.p) setTab('products'); else if (isTab(search.tab)) setTab(search.tab) }, [search.p, search.tab])
  const suppliers = useSuppliers(); const products = useProducts(); const versions = useProductVersions(); const batches = useBatches()
  if (!can('catalog.read')) return <section className="flex flex-col gap-4" data-testid="catalogue-page"><h1 className="text-2xl font-bold">{t('catalogue.title')}</h1><NotAllowed permission="catalog.read" /></section>
  if (!suppliers || !products || !versions || !batches) return <p>{t('common.loading')}</p>
  return (
    <section className="flex flex-col gap-4" data-testid="catalogue-page">
      <h1 className="text-2xl font-bold">{t('catalogue.title')}</h1>
      <p className="meta">{t('catalogue.help')}</p>
      <RowLink to="/catalogue/ephy" icon="leaf" title={t('ephy.title')} hint={t('ephy.linkHint')} testId="catalogue-ephy" />          {/* M21: the national list, not the club's catalogue */}
      {!online && <Banner tone="warn"><span data-testid="catalogue-offline">{t('catalogue.offline')}</span></Banner>}
      <div role="tablist" aria-label={t('catalogue.title')} className="grid grid-cols-3 gap-2">
        {TABS.map((id) => (
          <button key={id} type="button" role="tab" id={`catalogue-tab-${id}`} aria-selected={tab === id} aria-controls={`catalogue-panel-${id}`} tabIndex={tab === id ? 0 : -1} data-testid={`catalogue-tab-${id}`} onClick={() => setTab(id)}
            className={`btn !min-h-12 !px-2 text-sm ${tab === id ? 'btn-primary' : ''}`}>
            {t(`catalogue.tabs.${id}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`catalogue-panel-${tab}`} aria-labelledby={`catalogue-tab-${tab}`}>
        {tab === 'products' && <ProductsTab data={{ suppliers, products, versions, batches }} {...(focusId ? { focusId } : {})} />}
        {tab === 'stock' && <StockTab />}
        {tab === 'suppliers' && <SuppliersTab suppliers={suppliers} />}
      </div>
    </section>
  )
}
